import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import throttle from "lodash/throttle";
import { useState, useEffect, useCallback } from "react";

export type ScrollDirection = "up" | "down" | "left" | "right";
export interface ScrollState {
  /**
   * direction of the scroll up or down with y axis
   * and left or right for x axis
   *
   * @type {ScrollDirection}
   */
  scrollDirection: ScrollDirection;
  /**
   * abs difference from last scroll position
   *
   * @type {number}
   */
  scrollDifference: number;
  /**
   * current scroll position
   *
   * @type {number}
   */
  scrollPosition: number;
}

/**
 * Detect the scroll events on the window or target element using ref callback,
 * and will return scroll state (direction, difference, position, refCallBack),
 * set the parameter useRefCallBack to true to attach the scroll detector to the element
 *
 * @example
 * const { scrollDirection, scrollDifference, scrollPosition } = useDetectScroll();
 *
 * @example
 * const { scrollDirection, scrollDifference, scrollPosition } = useDetectScroll("x", 10);
 *
 * @param {("x" | "y")} [axis="y"]
 * @param {number} [threshold=0]
 * @returns {ScrollState}
 */
export const useDetectScroll = (
  axis: "x" | "y" = "y",
  threshold: number = 0
): ScrollState => {
  return useDetectScrollFunction(axis, threshold);
};

/**
 * Detect the scroll events on target element using ref callback,
 * and will return scroll state (direction, difference, position) and refCallBack,
 * set the parameter useRefCallBack to true to attach the scroll detector to the element
 *
 * @example
 * const { scrollState, refCallBack } = useDetectScroll("y", 0, true);
 * <div ref={refCallBack}>Test</div>
 *
 * @example
 * const { scrollState, refCallBack } = useDetectScroll("x", 0, true);
 * <div ref={refCallBack}>Test</div>
 *
 * @export
 * @param {("x" | "y")} axis
 * @param {number} threshold
 * @returns {{ refCallBack: (element: HTMLElement | null) => void, state: ScrollState }}
 */
export const useDetectRefScroll = <E extends HTMLElement>(
  axis: "x" | "y" = "y",
  threshold: number = 0
): { refCallBack: (element: E | null) => void; scrollState: ScrollState } => {
  return useDetectScrollFunction(axis, threshold, true);
};

function useDetectScrollFunction(
  axis: "x" | "y",
  threshold: number
): ScrollState;

function useDetectScrollFunction<E extends HTMLElement>(
  axis?: "x" | "y",
  threshold?: number,
  shouldUseRefCallBack?: boolean
): { refCallBack: (element: E | null) => void; scrollState: ScrollState };

function useDetectScrollFunction<E extends HTMLElement>(
  axis: "x" | "y" = "y",
  threshold: number = 0,
  shouldUseRefCallBack: boolean = false
):
  | ScrollState
  | { refCallBack: (element: E | null) => void; scrollState: ScrollState } {
  const [targetElement, setTargetElement] = useState<HTMLElement>();

  const refCallBack = useCallback((element: HTMLElement | null) => {
    if (element) {
      setTargetElement(element);
    }
  }, []);

  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollDirection: "down",
    scrollDifference: 0,
    scrollPosition: 0,
  });

  useEffect(() => {
    if (shouldUseRefCallBack && !targetElement) {
      return;
    }
    const thr = threshold > 0 ? threshold : 0;
    let ticking = false;
    let scrollTimeout: Timer | undefined;
    let lastScroll = 0;
    const window = getWindowOrThrow();
    if (shouldUseRefCallBack) {
      if (targetElement) {
        if (axis === "y") {
          lastScroll = targetElement.scrollTop;
        } else {
          lastScroll = targetElement.scrollLeft;
        }
      }
    } else {
      if (axis === "y") {
        lastScroll = window.scrollY;
      } else {
        lastScroll = window.scrollX;
      }
    }

    const updateScrollDir = () => {
      let scroll = 0;

      if (shouldUseRefCallBack) {
        if (targetElement) {
          if (axis === "y") {
            scroll = targetElement.scrollTop;
          } else {
            scroll = targetElement.scrollLeft;
          }
        }
      } else {
        if (axis === "y") {
          scroll = window.scrollY;
        } else {
          scroll = window.scrollX;
        }
      }

      if (Math.abs(scroll - lastScroll) < thr) {
        lastScroll = scroll > 0 ? scroll : 0;
        ticking = false;
        return;
      }
      let direction: ScrollDirection;
      if (axis === "y") {
        direction = scroll > lastScroll ? "down" : "up";
      } else {
        direction = scroll > lastScroll ? "right" : "left";
      }

      setScrollState({
        scrollDifference: Math.abs(lastScroll - scroll),
        scrollPosition: scroll,
        scrollDirection: direction,
      });

      lastScroll = scroll > 0 ? scroll : 0;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        scrollTimeout = setTimeout(updateScrollDir, 100);
        ticking = true;
      }
    };
    if (shouldUseRefCallBack) {
      if (targetElement) {
        targetElement.addEventListener("scroll", onScroll);
      }
    } else {
      window.addEventListener("scroll", onScroll);
    }

    return () => {
      if (shouldUseRefCallBack) {
        if (targetElement) {
          targetElement.removeEventListener("scroll", onScroll);
        }
      } else {
        window.removeEventListener("scroll", onScroll);
      }
    };
  }, [axis, scrollState, targetElement, threshold, shouldUseRefCallBack]);

  if (shouldUseRefCallBack) {
    return {
      refCallBack,
      scrollState,
    };
  } else {
    return scrollState;
  }
}
